import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { ScrollableFadeContainer } from "../../ui";
import { CompleteMarkdown } from "../internal/CompleteMarkdown";
import { LinearIcon } from "./linearIcon";
import { type LinearProject, type LinearProjectsPayload, parseLinearProjectsPayload } from "./linearProjectsSchema";
import { formatLinearDate, safeLinearUrl } from "./linearRenderUtils";

interface LinearProjectsRendererProps {
  block: StreamingBlock;
}

const ProjectTitle = (props: { project: LinearProject }) => (
  <div class="flex items-baseline justify-between gap-3">
    <Show
      when={safeLinearUrl(props.project.url)}
      fallback={<span class="truncate text-sm font-medium text-primary-text">{props.project.name}</span>}
    >
      {(href) => (
        <a
          href={href()}
          target="_blank"
          rel="noopener noreferrer"
          class="truncate text-sm font-medium text-brand hover:underline"
        >
          {props.project.name}
        </a>
      )}
    </Show>
    <Show when={props.project.targetDate}>
      {(targetDate) => <span class="shrink-0 text-xs text-muted-text">{formatLinearDate(targetDate())}</span>}
    </Show>
  </div>
);

const ProjectMeta = (props: { project: LinearProject }) => {
  const rt = useChatRuntime();

  const issueCountLabel = () => {
    const count = props.project.issueCount;
    if (count === undefined) return undefined;
    return count === 1
      ? rt.t("linear_projects_card.issues_one")
      : rt.t("linear_projects_card.issues", { count: String(count) });
  };

  return (
    <div class="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-text">
      <span class="shrink-0 rounded bg-inset px-1.5 py-0.5">{props.project.state}</span>
      <span class="shrink-0">
        {rt.t("linear_projects_card.progress", { percent: String(Math.round(props.project.progress * 100)) })}
      </span>
      <Show when={props.project.lead}>
        {(lead) => <span class="truncate">{rt.t("linear_projects_card.lead", { name: lead() })}</span>}
      </Show>
      <Show when={props.project.startDate}>
        {(startDate) => (
          <span class="shrink-0">{rt.t("linear_projects_card.started", { date: formatLinearDate(startDate()) })}</span>
        )}
      </Show>
      <Show when={issueCountLabel()}>{(label) => <span class="shrink-0">{label()}</span>}</Show>
      <For each={props.project.teams}>
        {(team) => <span class="truncate rounded bg-inset px-1.5 py-0.5">{team}</span>}
      </For>
    </div>
  );
};

export const LinearProjectsRenderer = (props: LinearProjectsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LinearProjectsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLinearProjectsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  // A "get one project" call answers with a single element carrying the detail
  // fields a listing omits, so it gets the expanded card.
  const onlyProject = () => {
    const projects = payload()?.projects ?? [];
    return projects.length === 1 ? projects[0] : undefined;
  };

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("linear_projects_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().projects.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <LinearIcon class="size-4 shrink-0 text-primary-text" />
                <span class="truncate text-sm font-medium text-primary-text">{rt.t("linear_projects_card.title")}</span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().projects.length === 1
                    ? rt.t("linear_projects_card.results_one")
                    : rt.t("linear_projects_card.results", { count: String(data().projects.length) })}
                </span>
              </div>

              <Show
                when={onlyProject()}
                fallback={
                  <ul class="divide-y divide-primary-border">
                    <For each={data().projects}>
                      {(project) => (
                        <li class="px-3 py-2">
                          <ProjectTitle project={project} />
                          <ProjectMeta project={project} />
                        </li>
                      )}
                    </For>
                  </ul>
                }
              >
                {(project) => (
                  <div class="px-3 py-2">
                    <ProjectTitle project={project()} />
                    <ProjectMeta project={project()} />
                    <Show when={project().description?.trim()}>
                      {(description) => (
                        <div class="mt-2">
                          <ScrollableFadeContainer maxHeight="200px">
                            <CompleteMarkdown markdown={description()} />
                          </ScrollableFadeContainer>
                        </div>
                      )}
                    </Show>
                  </div>
                )}
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
